import type { IconName } from '../ui/Icon'

/**
 * Samples assessed against profiles that ship with resqui, one set of
 * repositories per profile, so a user can see what each profile asks of a
 * project.
 *
 * All from resqui's main branch, run without a GitHub token, which OpenSSF
 * Scorecard and HowFairIs need. The three EVERSE tiers
 * (configurations/indicators_*.json) ask for RSFC and Scorecard results, so
 * without Scorecard a tier gives exactly the RSFC results of a complete run:
 * their sets are each repository's complete run with only that tier's
 * indicators kept (scripts/tier-samples.py), which spares GitHub's request
 * budget a second RSFC run. Complete shows all seven tools side by side.
 * resqui credits some checks to the wrong tool (UPSTREAM_ISSUES.md, issue 1);
 * each was restored from the tool resqui's own run log names for it.
 *
 * Each repository appears under one profile only: loading two sets together
 * must not turn one project into a run history of unrelated indicator sets.
 * Paths are relative to public/samples/.
 */
export interface SampleProfile {
  id: string
  name: string
  tag: string
  desc: string
  icon: IconName
  files: string[]
}

export const SAMPLE_PROFILES: SampleProfile[] = [
  {
    id: 'analysis_code',
    name: 'Analysis code',
    tag: '3 indicators',
    desc: 'rcrisp, neurogym',
    icon: 'document',
    files: ['profiles/analysis_code/rcrisp.json', 'profiles/analysis_code/neurogym.json'],
  },
  {
    id: 'prototype_tools',
    name: 'Prototype tools',
    tag: '10 indicators',
    desc: 'resqui, kernel_tuner',
    icon: 'layers',
    files: [
      'profiles/prototype_tools/QualityPipelines.json',
      'profiles/prototype_tools/kernel_tuner.json',
    ],
  },
  {
    id: 'rs_infrastructure',
    name: 'RS infrastructure',
    tag: '12 indicators',
    desc: 'DashVERSE, RSQKit, TechRadar, stdlib',
    icon: 'database',
    files: [
      'profiles/rs_infrastructure/DashVERSE.json',
      'profiles/rs_infrastructure/RSQKit.json',
      'profiles/rs_infrastructure/TechRadar.json',
      'profiles/rs_infrastructure/stdlib.json',
    ],
  },
  {
    id: 'complete',
    name: 'Complete',
    tag: '23 indicators',
    desc: 'Seven tools, from RSFC to SuperLinter, ASTMetrics and PySCN: Gridap.jl, STIR, pgmpy',
    icon: 'shield',
    files: [
      'profiles/complete/Gridap.jl.json',
      'profiles/complete/STIR.json',
      'profiles/complete/pgmpy.json',
    ],
  },
]
